import type { QueryClient } from "@tanstack/react-query";
import { QueryClientProvider } from "@tanstack/react-query";
import {
  createRootRouteWithContext,
  HeadContent,
  Outlet,
  Scripts,
  useRouter,
  useRouterState,
} from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { GlobalLoading } from "../components/GlobalLoading";
import { t } from "../i18n/en";
import { refreshRuntimeCopy } from "../i18n/runtime";
import { clearLegacyServiceWorker, STALE_ENTRY_RECOVERY_SCRIPT } from "../pwa";
import { initializeObservability, setAnalyticsConsent } from "../services/observability";
import { pageHead } from "../utils/page-title";
import { isStagingHostname } from "../utils/runtime-environment";
import "../index.css";
import "../mobile-foundation.css";

export interface RouterContext {
  queryClient: QueryClient;
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
  head: () => {
    const seo = pageHead("Human Design awareness", { canonicalPath: "/" });
    return {
      meta: [
        { charSet: "utf-8" },
        {
          name: "viewport",
          content: t("ui.copy_width_device_width_initial_scale_1_v_ee91fe46"),
        },
        { name: "theme-color", content: t("ui.copy_f7ede2_84f2355b") },
        { name: "apple-mobile-web-app-capable", content: "yes" },
        { name: "apple-mobile-web-app-status-bar-style", content: "default" },
        { name: "apple-mobile-web-app-title", content: "Harness" },
        { name: "format-detection", content: t("ui.copy_telephone_no_5de9ae3e") },
        ...seo.meta,
      ],
      links: [
        ...("links" in seo && seo.links ? seo.links : []),
        { rel: "manifest", href: "/manifest.webmanifest" },
        { rel: "icon", href: "/icons/harness-icon.png", type: "image/png" },
        { rel: "apple-touch-icon", href: "/icons/harness-icon.png" },
      ],
      scripts: [{ children: STALE_ENTRY_RECOVERY_SCRIPT }],
    };
  },
});

function RootLayout() {
  const router = useRouter();
  const { queryClient } = Route.useRouteContext();
  const [, setCopyVersion] = useState(0);
  const isRouterLoading = useRouterState({ select: (state) => state.isLoading });
  const [showRouterLoading, setShowRouterLoading] = useState(false);
  const [analyticsConsent, setAnalyticsConsentState] = useState<"unknown" | "granted" | "denied">(
    "unknown",
  );
  const isStaging = typeof window !== "undefined" && isStagingHostname(window.location.hostname);

  useEffect(() => {
    setAnalyticsConsentState(getStoredAnalyticsConsent());
    void initializeObservability(router);
    clearLegacyServiceWorker();
    void refreshRuntimeCopy();
    const copyRefreshInterval = window.setInterval(() => void refreshRuntimeCopy(), 5 * 60 * 1000);
    const handleCopyUpdate = () => setCopyVersion((version) => version + 1);
    window.addEventListener("harness:copy-updated", handleCopyUpdate);
    return () => {
      window.clearInterval(copyRefreshInterval);
      window.removeEventListener("harness:copy-updated", handleCopyUpdate);
    };
  }, [router]);

  useEffect(() => {
    if (!isRouterLoading) {
      setShowRouterLoading(false);
      return;
    }

    const timeoutId = window.setTimeout(() => setShowRouterLoading(true), 120);
    return () => window.clearTimeout(timeoutId);
  }, [isRouterLoading]);

  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body>
        <QueryClientProvider client={queryClient}>
          <div
            className={`harness-app relative min-h-screen w-full max-w-[100vw] overflow-x-clip bg-[#FAF0E6] text-[#090909] antialiased${
              isStaging ? " harness-app--staging" : ""
            }`}
          >
            <Outlet />
            <GlobalLoading
              visible={showRouterLoading}
              label={t("ui.copy_opening_your_next_page_2b10e864")}
            />
            {analyticsConsent === "unknown" ? (
              <aside
                aria-label={t("ui.copy_analytics_preference_0c0136bc")}
                className="pointer-events-none fixed inset-x-4 bottom-4 z-50 mx-auto flex max-w-3xl flex-col gap-3 rounded-2xl border border-black/10 bg-white p-4 shadow-xl sm:flex-row sm:items-center sm:justify-between"
              >
                <p className="pointer-events-none max-w-xl text-sm leading-5 text-black/75">
                  {t("ui.copy_we_use_optional_analytics_to_underst_4b9efb70")}
                </p>
                <div className="pointer-events-auto flex shrink-0 gap-2">
                  <button
                    className="rounded-full border border-black/20 px-4 py-2 text-sm font-medium"
                    onClick={() => {
                      setAnalyticsConsent(false);
                      setAnalyticsConsentState("denied");
                    }}
                    type="button"
                  >
                    {t("ui.copy_not_now_95573097")}
                  </button>
                  <button
                    className="rounded-full bg-black px-4 py-2 text-sm font-medium text-white"
                    onClick={() => {
                      setAnalyticsConsent(true);
                      setAnalyticsConsentState("granted");
                    }}
                    type="button"
                  >
                    {t("ui.copy_allow_analytics_7ba8f6c9")}
                  </button>
                </div>
              </aside>
            ) : null}
          </div>
        </QueryClientProvider>
        <Scripts />
      </body>
    </html>
  );
}

function getStoredAnalyticsConsent(): "unknown" | "granted" | "denied" {
  const value = window.localStorage.getItem("harness.analytics-consent");
  return value === "granted" || value === "denied" ? value : "unknown";
}
